import {
  combineLatest as observableCombineLatest,
  of as observableOf,
  Observable,
  Subject,
  combineLatest,
} from 'rxjs';

import {
  distinctUntilChanged,
  takeWhile,
  first,
  map,
  catchError,
  filter,
  mergeMap,
  startWith,
} from 'rxjs/operators';
import {ValuesObject} from '../detail-tabconfig';
import {UntypedFormControl, Validators} from '@angular/forms';
import {ChangeDetectorRef, Input, NgZone, Directive} from '@angular/core';
import {AbstractControlReadonlyComponent} from './abstract-control-readonly';
import {NestedObjectUpdateError} from '../../../error/errordefinitions';
import {ErrorService} from '../../../error/error.service';
import {ControlParentInterface} from '../control-parent.interface';

export class ControlTarget {
  constructor(
    public path: string,
    public source: string,
    public formatSave: (value: any) => any,
  ) {}
}

export class ControlTargetDontUpdateValue {}

@Directive()
export abstract class AbstractControlComponent extends AbstractControlReadonlyComponent {
  private _editEnabled: boolean;
  protected _editEnabledSubject = new Subject<boolean>();

  @Input()
  set editEnabled(editEnabled: boolean) {
    this._editEnabled = editEnabled;
    this._editEnabledSubject.next(editEnabled);
    if (editEnabled === true) {
      this.setDefaultValue();
    }
  }

  get editEnabled(): boolean {
    return this._editEnabled;
  }

  formControl: UntypedFormControl = null;

  private isDefaultValue: boolean = false;

  private initFinished: boolean = false;

  protected target: Observable<ControlTarget> = null;

  private freshResourceDataChanged: boolean = false;

  constructor(
    _parent: ControlParentInterface,
    sourceAlternateName: string = null,
    protected targetAlternateName: string = null,
    changeDetector: ChangeDetectorRef,
    private _ngZone: NgZone,
    errorService: ErrorService,
  ) {
    super(_parent, sourceAlternateName, changeDetector, errorService);
  }

  public get isEnabled(): Observable<boolean> {
    if (this._config != null) {
      return this._config.Enabled.pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        map(bool => bool && this.editEnabled),
      );
    }
    return observableOf(null);
  }

  public revalidate(includeNonTouched: boolean = false): Observable<boolean> {
    if (this.formControl.valid || (!includeNonTouched && !this.formControl.touched)) {
      return observableOf(true);
    } else if (this.formControl.pending) {
      return this.formControl.statusChanges.pipe(
        filter(status => status !== 'PENDING'),
        mergeMap(() => {
          return this._config.Enabled.pipe(
            catchError((error, caught) => this.handleError(error, caught)),
            first(),
            map(bool => {
              if (bool) {
                if (includeNonTouched) this.formControl.markAsTouched();
                this.changeDetector.markForCheck();
                return this.formControl.valid;
              } else {
                return true;
              }
            }),
          );
        }),
        first(),
      );
    } else {
      return this._config.Enabled.pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        first(),
        map(bool => {
          if (bool) {
            if (includeNonTouched) this.formControl.markAsTouched();
            this.changeDetector.markForCheck();
            return false;
          } else {
            return true;
          }
        }),
      );
    }
  }

  protected initMain(): void {
    this.formControl = new UntypedFormControl(null, null, null);
    if (this.sourceObs != null) {
      let firstValueOfSubscribe: boolean = true;
      this.sourceObs
        .pipe(
          map((value: any) => {
            let freshResource: boolean;
            if (firstValueOfSubscribe) {
              // force freshResource = true on first subscribe
              // defaultValue() can change freshResource during initialization
              // not sure if we need it, but does not harm?
              freshResource = true;
              firstValueOfSubscribe = false;
            } else {
              freshResource = this.freshResource;
            }
            value = this.formatSource(value);
            if (!this.compareValues(this.formControl.value, value)) {
              if (freshResource && this.formControl.value !== null) {
                this.freshResourceDataChanged = true;
              }
              this.formControl.setValue(value, {emitEvent: !freshResource});
              if (freshResource) {
                this.formControl.markAsUntouched();
                this.isDefaultValue = false;
              }
              this.changeDetector.markForCheck();
            }
            this.initFinished = true;
            if (this.editEnabled) {
              this.setDefaultValue();
            }
          }),
          catchError((err, caught) => this.handleError(err, caught)),
        )
        .subscribe();
    }
    this.initValidators();
    this.initTarget();
    if (this.editEnabled) {
      this.setDefaultValue();
    }

    combineLatest([this._config.Enabled, this._editEnabledSubject])
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        map(([config, edit]) => config && edit),
        startWith(false),
        takeWhile(() => this.alive),
      )
      .subscribe(enabled => {
        if (enabled !== !this.formControl.disabled) {
          if (enabled) {
            this.formControl.enable({onlySelf: true, emitEvent: true});
          } else {
            this.formControl.disable({onlySelf: true, emitEvent: false});
          }
        }
      });
  }

  private compareValues(val1: any, val2: any): boolean {
    if (Array.isArray(val1) !== Array.isArray(val2)) {
      return false;
    }

    if (Array.isArray(val1)) {
      if (val1.length !== val2.length) {
        return false;
      }
      if (val1.length === 0) {
        return true;
      }
      return JSON.stringify(val1.slice().sort()) === JSON.stringify(val2.slice().sort());
    } else {
      // TODO: do we use objects sometimes? compare them!

      return val1 === val2;
    }
  }

  protected formatSource(source: any): any {
    if (source == null) return '';
    return source;
  }

  protected initValidators(): void {
    if (this._config == null || this.formControl == null) {
      throw Error('Initialized target too early.');
    }

    this._config
      .getBooleanPropertyValues('Required')
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe(required => {
        if (required === true) {
          this.formControl.setValidators([Validators.required]);
        } else {
          this.formControl.clearValidators();
        }
        this.formControl.updateValueAndValidity({emitEvent: false});
      });
  }

  protected initTarget(): void {
    this._ngZone.runOutsideAngular(() => {
      if (this._config == null || this.formControl == null) {
        throw Error('Initialized target too early.');
      }

      let targetObs: Observable<ValuesObject<any>> = null;

      if (this._config.propertyExists('Target')) {
        targetObs = this._config.getProperty('Target');
      } else if (
        this.targetAlternateName != null &&
        this._config.propertyExists(this.targetAlternateName)
      ) {
        targetObs = this._config.getProperty(this.targetAlternateName);
      }

      if (targetObs != null) {
        this.target = targetObs.pipe(
          map((vo: ValuesObject<any>) => {
            return new ControlTarget(vo.BindingPath, vo.BindingSource, vo.formatSave);
          }),
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
          distinctUntilChanged(),
        );

        let valueChangeObs: Observable<any> = this.getValueChangedObs();

        observableCombineLatest(
          valueChangeObs,
          this.target.pipe(first()),
          (newValue: any, target: ControlTarget) => {
            if (newValue === '' && this.formControl.value !== '') {
              // Fix for IE: Fires update with '' when placeholder is null
              console.log(
                "FormControl fired event with empty value, but value of FormControl isn't empty. Don't update resource. (target path: " +
                  target.path,
              );
              return;
            }

            if (target.path == null || target.source == null) {
              this.getCaughtObservable(this._config.Name)
                .pipe(first())
                .subscribe(v => {
                  console.error('Target Path or Target Source is null for control ' + v.Values);
                });
              return;
            }

            if (this.isDefaultValue && this.formControl.touched) {
              this.isDefaultValue = false;
            }

            if (target.path.indexOf('/') !== -1) {
              const matches = /([a-zA-Z_:][a-zA-Z0-9-._:]*)\/([a-zA-Z_:][a-zA-Z0-9-._:]*)/.exec(
                target.path,
              );
              throw new NestedObjectUpdateError(matches[1], matches[2]);
            }

            newValue = this.formatTarget(newValue);
            if (target.formatSave != null && typeof target.formatSave === 'function') {
              newValue = target.formatSave(newValue);
            }
            if (target.source === 'object') {
              if (!(newValue instanceof ControlTargetDontUpdateValue)) {
                this._parent.updateResource(target.path, newValue);
              }
            } else if (target.source === 'schemaLocConfig') {
              if (!(newValue instanceof ControlTargetDontUpdateValue)) {
                this._parent.updateLocale(newValue);
              }
            } else if (target.source === 'localization') {
              if (!(newValue instanceof ControlTargetDontUpdateValue)) {
                this._parent.updateLocalizedValue(target.path, newValue);
              }
            } else {
              // TODO handle other bindings
              console.error(
                'Cannot update ' +
                  target.source +
                  ' binding. (path: ' +
                  target.path +
                  ', value: ' +
                  newValue +
                  ')',
              );
            }
          },
        )
          .pipe(
            catchError((error, caught) => this.handleError(error, caught)),
            takeWhile(() => this.alive),
          )
          .subscribe();
      }
    });
  }

  protected getValueChangedObs(): Observable<any> {
    return this.formControl.valueChanges.pipe(
      catchError((error, caught) => this.handleError(error, caught)),
      takeWhile(() => this.alive),
    );
  }

  protected formatTarget(value: any): any {
    if (value === '') return null;
    return value;
  }

  public setDefaultValue(): void {
    if (this._config == null || this.formControl == null) {
      throw Error('Called setDefaultValue too early.');
    }

    if (
      this.initFinished === true &&
      this.formatTarget(this.formControl.value) === null &&
      this._config.propertyExists('DefaultValue')
    ) {
      // can be the case if opened via direct link to wizard mask (freshResourceDataChanged false)
      this.isDefaultValue = true;
      this._config
        .getPropertyValues('DefaultValue')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.isDefaultValue),
        )
        .subscribe((value: any) => {
          try {
            value = this.formatSource(value);
          } catch (e) {
            this.handleError(e);
          }
          this.formControl.markAsUntouched();
          this.formControl.setValue(value, {emitEvent: true});
          this.changeDetector.markForCheck();
        });
    }
  }

  protected freshResourceChanged() {
    if (!this.freshResource && this.freshResourceDataChanged) {
      this.freshResourceDataChanged = false;
      this.formControl.setValue(this.formControl.value, {emitEvent: true});
    }
  }

  public refreshUI(): void {}

  public resetUI(): void {
    //used to reset formcontrol for new dialog
    this.formControl = new UntypedFormControl(null, null, null);
  }
}
